/*
 * Citera — global.js
 * Small, dependency-free UI layer: modal drawers (mobile nav + cart) and an
 * AJAX cart backed by Shopify's Cart + Section Rendering APIs. Deferred, no framework.
 */
(function () {
  "use strict";

  var CART_DRAWER_ID = "cart-drawer"; // the <dialog> + the section name requested
  var CART_CONTENT_ID = "cart-drawer-content"; // inner region swapped on every cart change

  /* ---------- Accessibility helpers ---------- */
  var strings = window.CiteraStrings || {};
  // Polite live region for status messages (cart updates, errors).
  var liveRegion;
  function announce(message) {
    if (!message) return;
    if (!liveRegion) {
      liveRegion = document.createElement("p");
      liveRegion.className = "visually-hidden";
      liveRegion.setAttribute("role", "status");
      liveRegion.setAttribute("aria-live", "polite");
      document.body.appendChild(liveRegion);
    }
    liveRegion.textContent = "";
    window.setTimeout(function () {
      liveRegion.textContent = message;
    }, 60);
  }

  /* ---------- Drawers (native <dialog>) ---------- */
  // Remember what opened each dialog so focus returns there on close (WCAG 2.4.3).
  var dialogInvoker = new WeakMap();
  // Depth counter, not a boolean: closing one dialog while another is still
  // open must not unlock background scroll.
  var openDialogCount = 0;

  function openDialog(dialog, invoker) {
    if (!dialog || dialog.open) return;
    dialogInvoker.set(dialog, invoker || document.activeElement);
    // The facet drawer is a modal on every viewport.
    if (dialog.id === "facet-drawer") dialog.setAttribute("role", "dialog");
    try {
      dialog.showModal();
    } catch (e) {
      // iOS Safari can throw here (e.g. a transformed ancestor). Open
      // non-modal and style it into place; a ::before supplies the backdrop.
      dialog.setAttribute("open", "");
      dialog.classList.add("dialog--fallback");
    }
    openDialogCount += 1;
    document.documentElement.style.overflow = "hidden";
    var focusable = dialog.querySelector(
      "[autofocus], [data-drawer-close], [data-lightbox-close]",
    );
    if (focusable) focusable.focus();
  }

  function closeDialog(dialog) {
    if (!dialog || !dialog.open) return;
    dialog.close();
  }

  document.addEventListener("click", function (event) {
    var opener = event.target.closest("[data-drawer-open]");
    if (opener) {
      event.preventDefault();
      var target = opener.getAttribute("data-drawer-open");
      openDialog(document.getElementById(target), opener);
      if (target === CART_DRAWER_ID) loadCartUpsell();
      return;
    }
    var closer = event.target.closest("[data-drawer-close]");
    if (closer) {
      event.preventDefault();
      closeDialog(closer.closest("dialog"));
    }
  });

  // Restore scroll + return focus whenever any dialog closes; backdrop click closes.
  document.querySelectorAll("dialog").forEach(function (dialog) {
    dialog.addEventListener("close", function () {
      dialog.classList.remove("dialog--fallback");
      openDialogCount = Math.max(0, openDialogCount - 1);
      if (openDialogCount === 0) document.documentElement.style.overflow = "";
      var invoker = dialogInvoker.get(dialog);
      dialogInvoker.delete(dialog);
      if (invoker && typeof invoker.focus === "function" && document.contains(invoker)) {
        invoker.focus();
      }
    });
    dialog.addEventListener("click", function (event) {
      if (event.target === dialog) closeDialog(dialog);
    });
  });

  /* ---------- Predictive search ---------- */
  (function () {
    var input = document.querySelector("[data-predictive-input]");
    var results = document.querySelector("[data-predictive-results]");
    if (!input || !results) return;
    var endpoint =
      input.getAttribute("data-predictive-url") ||
      window.Shopify.routes.root + "search/suggest";
    var status = document.querySelector("[data-predictive-status]");
    var timer;
    var controller;

    var options = [];
    var activeIndex = -1;

    function setExpanded(open) {
      input.setAttribute("aria-expanded", open ? "true" : "false");
    }

    function clearActive() {
      activeIndex = -1;
      input.removeAttribute("aria-activedescendant");
    }

    // Combobox plumbing: injected anchors become listbox options with ids so
    // aria-activedescendant + arrow keys can drive them; wrapper markup is
    // flattened to presentation inside the listbox.
    function collectOptions() {
      options = Array.prototype.slice.call(
        results.querySelectorAll(".predictive__query, .predictive__product, .predictive__view-all")
      );
      options.forEach(function (el, i) {
        el.id = "predictive-option-" + i;
        el.setAttribute("role", "option");
        el.setAttribute("aria-selected", "false");
      });
      results
        .querySelectorAll(".predictive, .predictive__group, .predictive__heading, ul, li")
        .forEach(function (el) {
          el.setAttribute("role", "presentation");
        });
      clearActive();
    }

    function setActive(index) {
      if (!options.length) return;
      if (activeIndex > -1 && options[activeIndex]) {
        options[activeIndex].classList.remove("is-active");
        options[activeIndex].setAttribute("aria-selected", "false");
      }
      activeIndex = (index + options.length) % options.length;
      var option = options[activeIndex];
      option.classList.add("is-active");
      option.setAttribute("aria-selected", "true");
      input.setAttribute("aria-activedescendant", option.id);
      if (option.scrollIntoView) option.scrollIntoView({ block: "nearest" });
    }

    function closeList() {
      results.innerHTML = "";
      options = [];
      setExpanded(false);
      if (status) status.textContent = "";
      clearActive();
    }

    function render(term) {
      // Abort before the short-term guard so a slower in-flight response for a
      // longer query can't repopulate a list the shopper already cleared.
      if (controller) controller.abort();
      if (term.length < 2) {
        closeList();
        return;
      }
      controller = new AbortController();
      var url =
        endpoint +
        "?q=" +
        encodeURIComponent(term) +
        "&section_id=predictive-search" +
        "&resources[type]=product,query&resources[limit]=6";
      fetch(url, { signal: controller.signal })
        .then(function (response) {
          return response.text();
        })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, "text/html");
          var section = doc.querySelector("#shopify-section-predictive-search");
          results.innerHTML = section ? section.innerHTML : "";
          collectOptions();
          // Announce a count, not the whole result DOM, on each keystroke.
          // Count only result anchors — counting wrapper <li>s too would double it.
          var count = results.querySelectorAll(".predictive__product, .predictive__query").length;
          setExpanded(count > 0);
          if (status) {
            var s = window.CiteraStrings || {};
            var tpl = count === 1 ? s.searchResultsOne || "[count] result" : s.searchResultsOther || "[count] results";
            status.textContent = tpl.replace("[count]", count);
          }
        })
        .catch(function () {});
    }

    input.addEventListener("input", function () {
      var term = input.value.trim();
      clearTimeout(timer);
      timer = setTimeout(function () {
        render(term);
      }, 220);
    });

    // Keyboard for the combobox: arrows move through options, Enter follows the
    // active one, Escape clears the list (a second Escape closes the drawer).
    input.addEventListener("keydown", function (event) {
      var open = input.getAttribute("aria-expanded") === "true" && options.length > 0;
      if (event.key === "ArrowDown") {
        if (!open) return;
        event.preventDefault();
        setActive(activeIndex + 1);
      } else if (event.key === "ArrowUp") {
        if (!open) return;
        event.preventDefault();
        setActive(activeIndex - 1);
      } else if (event.key === "Enter") {
        if (open && activeIndex > -1 && options[activeIndex]) {
          event.preventDefault();
          options[activeIndex].click();
        }
      } else if (event.key === "Escape") {
        if (open) {
          event.preventDefault();
          event.stopPropagation();
          closeList();
        }
      }
    });
  })();

  /* ---------- AJAX cart ---------- */
  function refreshCartDrawer(sectionsHTML) {
    var current = document.getElementById(CART_CONTENT_ID);
    var html = sectionsHTML && sectionsHTML[CART_DRAWER_ID];
    if (current && html) {
      var parsed = new DOMParser().parseFromString(html, "text/html");
      var next = parsed.getElementById(CART_CONTENT_ID);
      if (next) current.replaceWith(next); // swap inner region only, keep <dialog> open
    }
    updateCartCount();
    loadCartUpsell();
  }

  // In-cart upsell: fetch complementary recommendations for the first line item.
  function loadCartUpsell() {
    var box = document.querySelector("[data-cart-upsell]");
    if (!box) return;
    var pid = box.getAttribute("data-product-id");
    if (!pid) {
      box.innerHTML = "";
      return;
    }
    fetch(
      window.Shopify.routes.root +
        "recommendations/products?product_id=" +
        pid +
        "&limit=2&intent=related&section_id=cart-upsell",
    )
      .then(function (r) {
        return r.text();
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, "text/html");
        var section = doc.querySelector(".shopify-section");
        box.innerHTML = section ? section.innerHTML : "";
      })
      .catch(function () {});
  }

  // Cart note + gift attribute (persist without re-rendering the open drawer).
  function updateCartField(body) {
    fetch(window.Shopify.routes.root + "cart/update.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
  }
  document.addEventListener("change", function (event) {
    var note = event.target.closest("[data-cart-note]");
    if (note) {
      updateCartField({ note: note.value });
      return;
    }
    var gift = event.target.closest("[data-cart-gift]");
    if (gift) {
      updateCartField({ attributes: { Gift: gift.checked ? "Yes" : "" } });
    }
  });

  function updateCartCount() {
    fetch(window.Shopify.routes.root + "cart.js")
      .then(function (r) {
        if (!r.ok) throw new Error("cart.js " + r.status);
        return r.json();
      })
      .then(function (cart) {
        document.querySelectorAll("[data-cart-count]").forEach(function (el) {
          el.textContent = cart.item_count;
          el.hidden = cart.item_count === 0;
        });
      })
      .catch(function () {
        /* transient failure: keep the current badge, no unhandled rejection */
      });
  }

  function postCart(endpoint, body) {
    body.sections = [CART_DRAWER_ID];
    body.sections_url = window.location.pathname;
    return fetch(window.Shopify.routes.root + endpoint, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify(body),
    }).then(function (r) {
      // Non-JSON responses (5xx/HTML error pages) surface as an error object the
      // callers already handle, instead of an opaque parse rejection.
      return r.json().catch(function () {
        return { status: r.status, description: strings.cartError };
      });
    });
  }

  // Inline cart error next to a form + a polite announcement.
  function showCartError(form, message) {
    var text = message || strings.cartError || "Produsul nu a putut fi adăugat în coș.";
    var box = form.querySelector("[data-cart-error]");
    if (!box) {
      box = document.createElement("p");
      box.setAttribute("data-cart-error", "");
      box.setAttribute("role", "alert");
      box.className = "cart-error";
      form.appendChild(box);
    }
    box.textContent = text;
    announce(text);
  }
  function clearCartError(form) {
    var box = form.querySelector("[data-cart-error]");
    if (box) box.remove();
  }

  // Build a cart item from the full form: id + quantity + selling_plan +
  // properties[...] (gift-card recipient etc). Dropping these silently was a
  // real bug — the payload must mirror what a native (non-AJAX) submit sends.
  function formToItem(formData) {
    var item = {
      id: formData.get("id"),
      quantity: Number(formData.get("quantity") || 1),
    };
    var plan = formData.get("selling_plan");
    if (plan) item.selling_plan = Number(plan);
    var props = {};
    formData.forEach(function (value, key) {
      var m = key.match(/^properties\[(.+)\]$/);
      if (m && value !== "") props[m[1]] = value;
    });
    if (Object.keys(props).length) item.properties = props;
    return item;
  }

  // Intercept add-to-cart forms so the drawer opens instead of a full page load.
  document.addEventListener("submit", function (event) {
    var form = event.target.closest("form[data-cart-form]");
    if (!form) return;
    event.preventDefault();
    var button = event.submitter || form.querySelector('[type="submit"]');
    var buyNow = Boolean(button && button.matches("[data-buy-now]"));
    // Guard re-entry: a double click / held Enter must not add the item twice.
    if (button) {
      if (button.disabled) return;
      button.setAttribute("aria-busy", "true");
      button.disabled = true;
    }
    clearCartError(form);

    var formData = new FormData(form);
    var body = { items: [formToItem(formData)] };
    postCart("cart/add.js", body)
      .then(function (data) {
        if (data && (data.status || data.description)) {
          showCartError(form, data.description || data.message);
          return;
        }
        if (buyNow) {
          window.location.assign(window.Shopify.routes.root + "checkout");
          return;
        }
        // Success: if this form lives in the quick view, close it now (not on
        // submit — closing early would hide its own add-to-cart error).
        var qv = form.closest("#quick-view");
        if (qv) closeDialog(qv);
        refreshCartDrawer(data.sections);
        openDialog(document.getElementById(CART_DRAWER_ID), button);
        announce(strings.cartAdded || "Produs adăugat în coș");
      })
      .catch(function () {
        showCartError(form);
      })
      .finally(function () {
        if (button) {
          button.removeAttribute("aria-busy");
          button.disabled = false;
        }
      });
  });

  // Quantity change + remove inside the drawer (delegated).
  document.addEventListener("change", function (event) {
    var input = event.target.closest("[data-cart-quantity]");
    if (!input) return;
    postCart("cart/change.js", {
      line: Number(input.getAttribute("data-line")),
      quantity: Number(input.value),
    })
      .then(function (data) {
        if (data && data.status) {
          announce(data.description || data.message || strings.cartError);
          return;
        }
        refreshCartDrawer(data.sections);
        announce(strings.cartUpdated || "Coș actualizat");
      })
      .catch(function () {
        announce(strings.cartError || "Coșul nu a putut fi actualizat");
      });
  });

  document.addEventListener("click", function (event) {
    var remove = event.target.closest("[data-cart-remove]");
    if (!remove) return;
    event.preventDefault();
    postCart("cart/change.js", {
      line: Number(remove.getAttribute("data-line")),
      quantity: 0,
    })
      .then(function (data) {
        if (data && data.status) {
          announce(data.description || data.message || strings.cartError);
          return;
        }
        refreshCartDrawer(data.sections);
        announce(strings.cartUpdated || "Coș actualizat");
      })
      .catch(function () {
        announce(strings.cartError || "Coșul nu a putut fi actualizat");
      });
  });

  // Stepper +/- buttons: adjust the sibling quantity input, then fire its change.
  document.addEventListener("click", function (event) {
    var step = event.target.closest("[data-qty-step]");
    if (!step) return;
    var input = step.parentElement.querySelector("[data-cart-quantity]");
    if (!input) return;
    var next = Math.max(0, Number(input.value) + Number(step.getAttribute("data-qty-step")));
    input.value = next;
    input.dispatchEvent(new Event("change", { bubbles: true }));
  });

  /* ---------- Reducere de bun venit: aplicare automata a codului ----------
   * Shopify nu permite limita "o utilizare per client" pe reducerile automate,
   * doar pe coduri. Ca sa nu ceara nimanui sa tasteze codul, il aplicam singuri
   * printr-un GET pe /discount/COD, care seteaza cookie-ul de reducere pe cos.
   * Folosim ?redirect=/cart.js ca raspunsul sa fie cosul in JSON (~2 KB) si nu
   * pagina de start, deci avem si confirmarea aplicarii in acelasi apel.
   *
   * Eligibilitatea pe client vine din Liquid (snippets/welcome-discount.liquid).
   * Aici mai verificam doua lucruri, pe date proaspete:
   *   - cosul nu are deja alta reducere (nu suprascriem un cod mai bun)
   *   - nu am incercat deja in sesiunea asta (altfel am relua la fiecare pagina)
   */
  (function () {
    var config = window.CiteraWelcomeDiscount || {};
    if (!config.auto || !config.code) return;

    var FLAG = "citera:welcome-discount";
    var attempted = false;
    try {
      attempted = window.sessionStorage.getItem(FLAG) === "1";
    } catch (e) {
      // Mod privat sau storage blocat: mergem inainte, cel mai rau reluam apelul.
    }
    function markAttempted() {
      attempted = true;
      try {
        window.sessionStorage.setItem(FLAG, "1");
      } catch (e) {}
    }

    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || "/";
    var pending = null;

    // Returneaza o promisiune care se rezolva cu true daca reducerea e acum pe cos.
    function apply() {
      if (pending) return pending;
      if (attempted) return Promise.resolve(false);
      markAttempted();

      pending = fetch(root + "cart.js", { credentials: "same-origin" })
        .then(function (r) {
          return r.ok ? r.json() : null;
        })
        .then(function (cart) {
          // Deja are un cod (al nostru sau altul): nu atingem nimic.
          if (!cart || (cart.discount_codes && cart.discount_codes.length > 0)) return false;
          var url =
            root +
            "discount/" +
            encodeURIComponent(config.code) +
            "?redirect=" +
            encodeURIComponent(root + "cart.js");
          return fetch(url, { credentials: "same-origin" })
            .then(function (r) {
              return r.ok ? r.json() : null;
            })
            .then(function (updated) {
              if (!updated || !updated.discount_codes) return false;
              return updated.discount_codes.some(function (d) {
                return d.applicable;
              });
            });
        })
        .catch(function () {
          return false; // Reducerea e un bonus, nu blocam nimic daca a picat.
        });

      return pending;
    }

    // Markup-ul sertarului vine din Liquid, randat inainte ca reducerea sa intre
    // pe cos, deci dupa o aplicare reusita trebuie re-cerut o singura data.
    var refreshed = false;
    function applyAndRefresh() {
      return apply().then(function (applied) {
        if (!applied || refreshed) return;
        refreshed = true;
        return postCart("cart/update.js", {}).then(function (res) {
          if (res && res.sections) refreshCartDrawer(res.sections);
        });
      });
    }

    // Pe incarcarea paginii, cat timp nimeni nu se uita.
    if (document.readyState === "complete") {
      applyAndRefresh();
    } else {
      window.addEventListener("load", function () {
        applyAndRefresh();
      });
    }

    // Plasa de siguranta: daca omul deschide cosul inainte sa se termine apelul
    // de mai sus (sau acela a picat), reluam si reimprospatam.
    document.addEventListener(
      "click",
      function (event) {
        var opener = event.target.closest("[data-drawer-open]");
        if (!opener) return;
        if (opener.getAttribute("data-drawer-open") !== CART_DRAWER_ID) return;
        applyAndRefresh();
      },
      true,
    );
  })();

  /* ---------- Shared surface for per-template bundles (product.js etc.) ---------- */
  window.Citera = {
    strings: strings,
    announce: announce,
    openDialog: openDialog,
    postCart: postCart,
    formToItem: formToItem,
    showCartError: showCartError,
    clearCartError: clearCartError,
    refreshCartDrawer: refreshCartDrawer,
    cartDrawerId: CART_DRAWER_ID,
  };

  /* ---------- Marquee pause/play (WCAG 2.2.2) ---------- */
  document.addEventListener("click", function (event) {
    var toggle = event.target.closest("[data-marquee-toggle]");
    if (!toggle) return;
    var marquee = toggle.closest(".marquee");
    if (!marquee) return;
    var paused = marquee.classList.toggle("is-paused");
    var label = paused ? toggle.dataset.labelPlay : toggle.dataset.labelPause;
    if (label) toggle.setAttribute("aria-label", label);
  });

  /* ---------- Marquee: fill the viewport so the loop never leaves a gap ---------- */
  (function () {

    function fill(track) {
      // Drop clones from a previous run before remeasuring.
      track.querySelectorAll("[data-marquee-clone]").forEach(function (clone) {
        clone.remove();
      });
      var groups = track.querySelectorAll(".marquee__group, .brand-rail__list");
      if (!groups.length) return;
      var first = groups[0];
      var groupWidth = first.getBoundingClientRect().width;
      var marquee = track.closest(".marquee, .brand-rail");
      var viewportElement = marquee
        ? marquee.querySelector(".marquee__viewport, .brand-rail__viewport") || marquee
        : track;
      var viewport = viewportElement.getBoundingClientRect().width;
      if (!groupWidth || !viewport) return;
      // Enough groups to always cover the viewport plus one to scroll into.
      var needed = Math.ceil(viewport / groupWidth) + 1;
      var count = track.querySelectorAll(".marquee__group, .brand-rail__list").length;
      while (count < needed) {
        var clone = first.cloneNode(true);
        clone.setAttribute("inert", "");
        clone.setAttribute("aria-hidden", "true");
        clone.setAttribute("data-marquee-clone", "");
        track.appendChild(clone);
        count++;
      }
      // Shift by exactly one group width => a perfect loop at any width.
      track.style.setProperty("--marquee-shift", "-" + groupWidth + "px");
    }

    function run() {
      // Re-query each run — the theme editor injects new marquees live, and
      // fill() is idempotent (drops its clones before remeasuring).
      document.querySelectorAll(".marquee__track, .brand-rail__track").forEach(fill);
    }
    run();

    var resizeTimer;
    window.addEventListener("resize", function () {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(run, 200);
    });
    document.addEventListener("shopify:section:load", run);
  })();

  /* ---------- Countdown timers (real deadline, no evergreen) ---------- */
  function pad(n) {
    return n < 10 ? "0" + n : String(n);
  }

  function tickCountdown(el) {
    var end = new Date(el.getAttribute("data-countdown-end")).getTime();
    var remaining = end - Date.now();
    var units = el.querySelector("[data-countdown-units]");
    var expired = el.querySelector("[data-countdown-expired]");

    if (isNaN(end)) return true; // bad date -> stop, leave placeholders
    if (remaining <= 0) {
      if (units) units.hidden = true;
      if (expired) expired.hidden = false;
      return true; // done
    }

    var secs = Math.floor(remaining / 1000);
    var days = Math.floor(secs / 86400);
    var hours = Math.floor((secs % 86400) / 3600);
    var mins = Math.floor((secs % 3600) / 60);
    var s = secs % 60;
    set(el, "[data-countdown-days]", days);
    set(el, "[data-countdown-hours]", pad(hours));
    set(el, "[data-countdown-minutes]", pad(mins));
    set(el, "[data-countdown-seconds]", pad(s));
    return false;
  }

  function set(root, sel, val) {
    var node = root.querySelector(sel);
    if (node) node.textContent = val;
  }

  var countdowns = Array.prototype.slice.call(document.querySelectorAll("[data-countdown]"));
  function runCountdowns() {
    countdowns = countdowns.filter(function (el) {
      return !tickCountdown(el);
    });
    if (!countdowns.length && window._citeraCountdown) {
      clearInterval(window._citeraCountdown);
      window._citeraCountdown = null;
    }
  }
  function startCountdowns() {
    if (!countdowns.length) return;
    runCountdowns();
    if (countdowns.length && !window._citeraCountdown) {
      window._citeraCountdown = setInterval(runCountdowns, 1000);
    }
  }
  startCountdowns();
  // Theme editor: pick up countdowns in sections added/re-rendered live.
  document.addEventListener("shopify:section:load", function (event) {
    event.target.querySelectorAll("[data-countdown]").forEach(function (el) {
      if (countdowns.indexOf(el) === -1) countdowns.push(el);
    });
    startCountdowns();
  });

  /* ---------- Quick view (Section Rendering API into a modal) ---------- */
  var qvDialog = document.getElementById("quick-view");
  if (qvDialog) {
    var qvContent = qvDialog.querySelector("[data-quick-view-content]");
    document.addEventListener("click", function (event) {
      var btn = event.target.closest("[data-quick-view]");
      if (!btn) return;
      event.preventDefault();
      var handle = btn.getAttribute("data-quick-view");
      qvContent.innerHTML = "";
      openDialog(qvDialog);
      fetch(window.Shopify.routes.root + "products/" + handle + "?section_id=quick-view")
        .then(function (r) {
          return r.text();
        })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, "text/html");
          var inner = doc.querySelector(".qv");
          qvContent.innerHTML = inner ? inner.outerHTML : html;
        })
        .catch(function () {
          closeDialog(qvDialog);
        });
    });
    // Single-variant selector updates the displayed price.
    qvDialog.addEventListener("change", function (event) {
      var sel = event.target.closest("[data-qv-variant]");
      if (!sel) return;
      var opt = sel.options[sel.selectedIndex];
      var priceEl = qvDialog.querySelector("[data-qv-price]");
      if (opt && priceEl && opt.dataset.price) priceEl.textContent = opt.dataset.price;
    });
    // Closing on add-to-cart is handled by the global submit handler — only on
    // SUCCESS, so a failed add can show its error inside the still-open modal.
  }

  /* ---------- Sticky header: fix to top on scroll, with a spacer so content doesn't jump ---------- */
  var stickyHeader = document.querySelector("[data-header-sticky]");
  if (stickyHeader) {
    var headerSpacer = document.createElement("div");
    var headerStuck = false;
    var headerBaseTop = stickyHeader.getBoundingClientRect().top + window.scrollY;
    var updateStuck = function () {
      if (!headerStuck && window.scrollY > headerBaseTop + 4) {
        headerSpacer.style.height = stickyHeader.offsetHeight + "px";
        stickyHeader.parentNode.insertBefore(headerSpacer, stickyHeader);
        stickyHeader.classList.add("is-stuck");
        headerStuck = true;
      } else if (headerStuck && window.scrollY <= headerBaseTop) {
        stickyHeader.classList.remove("is-stuck");
        if (headerSpacer.parentNode) headerSpacer.parentNode.removeChild(headerSpacer);
        headerStuck = false;
      }
    };
    updateStuck();
    window.addEventListener("scroll", updateStuck, { passive: true });
    window.addEventListener(
      "resize",
      function () {
        if (!headerStuck) headerBaseTop = stickyHeader.getBoundingClientRect().top + window.scrollY;
      },
      { passive: true },
    );
  }

  /* ---------- Reveal sections on scroll (respects motion + reduced-motion) ---------- */
  var reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  if (
    document.body.classList.contains("anim-on") &&
    !reduceMotion &&
    "IntersectionObserver" in window
  ) {
    var reveals = Array.prototype.slice
      .call(document.querySelectorAll("#main-content > .shopify-section"))
      .filter(function (el) {
        // A continuously moving rail should not also fade in. Combining the
        // two animations caused the logo row to flash as it entered view.
        if (el.querySelector(".brand-rail")) return false;
        // Never hide anything already in the first viewport (protects the LCP
        // section whatever its index — the homepage can lead with a marquee/bar).
        return el.getBoundingClientRect().top >= window.innerHeight;
      });
    reveals.forEach(function (el) {
      el.classList.add("reveal");
    });
    var revealObserver = new IntersectionObserver(
      function (entries, obs) {
        entries.forEach(function (e) {
          if (e.isIntersecting) {
            e.target.classList.add("reveal--in");
            obs.unobserve(e.target);
          }
        });
      },
      { rootMargin: "0px 0px -8% 0px" },
    );
    reveals.forEach(function (el) {
      revealObserver.observe(el);
    });
  }

  /* ---------- Faceted filters: submit on change ---------- */
  document.addEventListener("change", function (event) {
    var form = event.target.closest("[data-facets]");
    if (!form) return;
    if (!(event.target.matches("input") || event.target.matches("select"))) return;
    // The filter drawer collects multiple selections on every viewport, then
    // submits once via "Apply". Sorting still applies immediately.
    if (form.hasAttribute("data-facets-filter")) return;
    form.submit();
  });

  /* ---------- Filter drawer: navigate on Apply ----------
     A <form> inside an open modal <dialog> does not reliably submit/navigate on
     iOS Safari (the submit fires but the GET navigation never happens). Build
     the URL from the form and navigate explicitly. Handle both the click
     (fires reliably on iOS) and submit (Android / Enter in the price inputs). */
  function navigateFacets(form) {
    var params = new URLSearchParams();
    new FormData(form).forEach(function (value, key) {
      if (value !== "") params.append(key, value);
    });
    var query = params.toString();
    window.location.assign(query ? location.pathname + "?" + query : location.pathname);
  }
  document.addEventListener("click", function (event) {
    var btn = event.target.closest(".facet-drawer__apply, .facets__apply");
    if (!btn) return;
    var form = btn.closest("form[data-facets-filter]");
    if (!form) return;
    event.preventDefault();
    navigateFacets(form);
  });
  document.addEventListener("submit", function (event) {
    var form = event.target.closest("form[data-facets-filter]");
    if (!form) return;
    event.preventDefault();
    navigateFacets(form);
  });

  /* ---------- Faceted filters: only one dropdown open at a time ---------- */
  // The `toggle` event does not bubble, so listen in the capture phase.
  document.addEventListener(
    "toggle",
    function (event) {
      var facet = event.target;
      if (facet.tagName !== "DETAILS" || !facet.classList.contains("facet") || !facet.open) return;
      var bar = facet.closest(".facets__bar");
      if (!bar) return;
      bar.querySelectorAll("details.facet[open]").forEach(function (other) {
        if (other !== facet) other.open = false;
      });
    },
    true,
  );

  // Close any open facet when clicking outside the filter bar.
  document.addEventListener("click", function (event) {
    if (event.target.closest(".facet")) return;
    document.querySelectorAll("details.facet[open]").forEach(function (facet) {
      facet.open = false;
    });
  });

  /* ---------- Header dropdowns: dismiss on outside-click + Escape ---------- */
  document.addEventListener("click", function (event) {
    if (event.target.closest(".header__dropdown")) return;
    document
      .querySelectorAll("details.header__dropdown[open]")
      .forEach(function (dd) {
        dd.open = false;
      });
  });
  document.addEventListener("keydown", function (event) {
    if (event.key !== "Escape") return;
    var open = document.querySelector("details.header__dropdown[open]");
    if (!open) return;
    open.open = false;
    var summary = open.querySelector("summary");
    if (summary) summary.focus();
  });

  /* ---------- Header dropdowns: open on hover (pointer devices only) ---------- */
  (function () {
    var hoverMedia = window.matchMedia("(hover: hover) and (pointer: fine)");
    if (!hoverMedia.matches) return;
    var CLOSE_DELAY = 180;

    document.querySelectorAll(".header__menu-item").forEach(function (item) {
      var dropdown = item.querySelector(":scope > details.header__dropdown");
      if (!dropdown) return;
      var closeTimer = null;

      item.addEventListener("mouseenter", function () {
        clearTimeout(closeTimer);
        document
          .querySelectorAll("details.header__dropdown[open]")
          .forEach(function (other) {
            if (other !== dropdown) other.open = false;
          });
        dropdown.open = true;
      });
      item.addEventListener("mouseleave", function () {
        closeTimer = setTimeout(function () {
          dropdown.open = false;
        }, CLOSE_DELAY);
      });
    });
  })();

  /* ---------- Acordeoane produs: deschidere/inchidere animata ---------- */
  (function () {
    var SELECTOR =
      "details.product__collapsible, details.product__description-accordion, details.product-optical-specs";
    var DURATION = 280;
    var EASING = "cubic-bezier(0.22, 0.61, 0.36, 1)";
    var reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)");

    function panelOf(details) {
      for (var i = 0; i < details.children.length; i++) {
        if (details.children[i].tagName !== "SUMMARY") return details.children[i];
      }
      return null;
    }

    function run(details, panel, opening) {
      if (details._accordionAnim) details._accordionAnim.cancel();
      var height = panel.scrollHeight;
      panel.style.overflow = "hidden";
      if (opening) details.open = true;
      else details.classList.add("is-closing");

      var frames = {
        height: opening ? ["0px", height + "px"] : [height + "px", "0px"],
        opacity: opening ? [0, 1] : [1, 0],
        transform: opening ? ["translateY(-6px)", "translateY(0)"] : ["translateY(0)", "translateY(-6px)"]
      };
      var anim = panel.animate(frames, { duration: DURATION, easing: EASING });
      details._accordionAnim = anim;
      anim.onfinish = function () {
        details._accordionAnim = null;
        panel.style.overflow = "";
        if (!opening) {
          details.open = false;
          details.classList.remove("is-closing");
        }
      };
      anim.oncancel = function () {
        panel.style.overflow = "";
        details.classList.remove("is-closing");
      };
    }

    document.addEventListener("click", function (event) {
      var summary = event.target.closest("summary");
      if (!summary) return;
      var details = summary.parentElement;
      if (!details || !details.matches(SELECTOR)) return;
      if (reduceMotion.matches) return;
      var panel = panelOf(details);
      if (!panel || typeof panel.animate !== "function") return;
      event.preventDefault();
      run(details, panel, !details.open);
    });
  })();

  /* ---------- Customer accounts: recover toggle, address forms, country default ---------- */
  (function () {
    function showRecover(name) {
      document.querySelectorAll("[data-recover-panel]").forEach(function (panel) {
        panel.hidden = panel.getAttribute("data-recover-panel") !== name;
      });
    }
    if (
      window.location.hash === "#recover" &&
      document.querySelector('[data-recover-panel="recover"]')
    ) {
      showRecover("recover");
    }

    document.addEventListener("click", function (event) {
      if (event.target.closest("[data-recover-show]")) return showRecover("recover");
      if (event.target.closest("[data-recover-hide]")) return showRecover("login");

      var newToggle = event.target.closest("[data-address-new-toggle]");
      if (newToggle) {
        var box = document.querySelector("[data-address-new]");
        if (box) box.hidden = !box.hidden;
        return;
      }
      var editToggle = event.target.closest("[data-address-edit-toggle]");
      if (editToggle) {
        var form = document.querySelector(
          '[data-address-edit="' + editToggle.getAttribute("data-address-edit-toggle") + '"]',
        );
        if (form) form.hidden = !form.hidden;
        return;
      }
      var del = event.target.closest("[data-address-delete]");
      if (del && del.dataset.confirm && !window.confirm(del.dataset.confirm)) {
        event.preventDefault();
      }
    });

    // Pre-select country selects from their data-default (no legacy Shopify lib).
    document.querySelectorAll("select[data-default]").forEach(function (select) {
      if (select.dataset.default) select.value = select.dataset.default;
    });
  })();

  /* ---------- Back to top ---------- */
  var toTop = document.querySelector("[data-back-to-top]");
  if (toTop) {
    var onTopScroll = function () {
      toTop.hidden = window.scrollY < 600;
    };
    onTopScroll();
    window.addEventListener("scroll", onTopScroll, { passive: true });
    toTop.addEventListener("click", function () {
      window.scrollTo({ top: 0, behavior: reduceMotion ? "auto" : "smooth" });
    });
  }
})();
